import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  TemplateRef,
  ViewChild, ViewContainerRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faArrowRightFromBracket, faBars, faBell, faBook, faChevronDown, faCircle, faCircleQuestion, faMagnifyingGlass, faSchool, faUser, faUserSecret } from '@fortawesome/free-solid-svg-icons';
import { DialogCloseResult } from '@progress/kendo-angular-dialog';
import { DropDownFilterSettings } from '@progress/kendo-angular-dropdowns';
import { SelectEvent, TabAlignment } from '@progress/kendo-angular-layout';
import { ContextMenuSelectEvent } from '@progress/kendo-angular-menu';
import { NotificationService } from '@progress/kendo-angular-notification';
import { PopoverAnchorDirective } from '@progress/kendo-angular-tooltip';
import {
  bellIcon,
  logoutIcon,
  menuIcon,
  questionCircleIcon,
  searchIcon,
  SVGIcon,
} from '@progress/kendo-svg-icons';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { orderBy, some } from 'lodash';
import {
  BehaviorSubject,
  catchError,
  filter,
  map,
  of,
  take,
  takeUntil,
} from 'rxjs';
import { AgencyList } from 'src/app/modules/dashboard/interface/agency-list';
import { AllNotificationsResponse, NotificationSummaryResponse } from 'src/app/modules/dashboard/interface/get-all-notifications-response';
import { MarkPayload } from 'src/app/modules/dashboard/interface/mark-payload';
import { AdminService } from 'src/app/modules/dashboard/services/admin.service';
import { DashboardService } from 'src/app/modules/dashboard/services/dashboard.service';
import { MarkingWarnService } from 'src/app/modules/dashboard/services/marking-warn.service';
import { MarkReadNotificationService } from 'src/app/modules/dashboard/services/notification.service';
import { AuthService } from 'src/app/services/auth.service';
import { UserService } from 'src/app/services/user.service';
import { AbstractComponent } from 'src/app/shared/components/abstract-component';
import { Constants } from 'src/app/shared/constants/constants';
import { dialogAnimationDuration, dialogTimer } from 'src/app/shared/constants/dialog-specs';
import { referenceDataFilterSettings } from 'src/app/shared/constants/reference-data-filter-settings';
import {
  maxBadgeCountLimit,
  NotificationAction,
  NotificationReadAllBit,
  NotificationType,
} from 'src/app/shared/enums/notification';
import { Permissions } from 'src/app/shared/enums/permissions';
import { formatNotificationDateTime } from 'src/app/shared/functions/format-notifications-datetime';
import { UserMenu } from 'src/app/shared/interfaces/user-menu';
import { Location } from 'src/app/shared/models/location';
import { Permission } from 'src/app/shared/models/permission';
import { User } from 'src/app/shared/models/user';
import { UserRole } from 'src/app/shared/models/user-role';
import { AuthStore } from 'src/app/stores/auth.store';
import { AppConfigService } from 'src/app/services/app-config.service';
import { IAppConfig } from 'src/app/shared/interfaces/app-config';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HeaderComponent extends AbstractComponent implements OnInit {
  public menuIcon: SVGIcon = menuIcon;
  public searchIcon: SVGIcon = searchIcon;
  public bellIcon: SVGIcon = bellIcon;
  public questionCircleIcon: SVGIcon = questionCircleIcon;
  public logoutIcon: SVGIcon = logoutIcon;
  public kendokaAvatar: string =
    'https://www.telerik.com/kendo-angular-ui-develop/components/navigation/appbar/assets/kendoka-angular.png';

  public show: boolean = false;
  public user: User;
  role: UserRole;
  roles: UserRole[];
  location: Location;
  locations: Location[];
  agencies: Location[];
  filterSettings: DropDownFilterSettings = referenceDataFilterSettings;

  @ViewChild('impersonationTemplate') impersonationTemplate: TemplateRef<any>;
  public alignment: TabAlignment = 'start';
  @ViewChild('anchor', { static: false })
  public anchor: PopoverAnchorDirective;
  selectedTabIndex: number = 0;
  allNotifications: AllNotificationsResponse[] = [];
  actionRequired: AllNotificationsResponse[] = [];
  informational: AllNotificationsResponse[] = [];
  zeroUnreadActionReqZeroUnreadInfo: boolean = false;
  zeroUnreadActionReqOnePlusUnreadInfo: boolean = false;
  onePlusUnreadActionReq: boolean = false;
  badgeCountToShow: number | null | string = null;
  searchData = ['Student', 'Location'];
  searchMode: string = 'Student';
  private tabIndexZero = 0;
  private emptyCount: number = 0;
  private dotBadgeCount: number = 0;
  private actionRequiredTab: number = 0;
  private tabIndexOne: number = 1;
  private startIndexPortion: number = 0;
  private endIndexPortion: number = 10;
  public actionRequiredLength: number = 0;
  public informationalLength: number = 0;
  loading$ = new BehaviorSubject<boolean>(false);
  showNewReferralModal$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  isNewReferral: boolean = true;
  enableNotifications: boolean = true;
  isPopoverOpen: boolean = false;
  displayImpersonateUserDialog$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  items: UserMenu[] = [];
  userInfoSectionOpen: boolean = false;
  searchText: string = '';

  readonly staffProfile: string = 'View My Staff Profile';
  readonly locationProfile: string = 'View My Location Profile';
  readonly startImpersonationText: string = 'Start Impersonation Session';
  readonly stopImpersonationText: string = 'Stop Impersonation Session';
  readonly logOut: string = 'Log Out';

  showNonProdBanner: boolean = false;
  nonProdEnvironmentLabel: string = 'LOWER ENVIRONMENT';

  constructor(
    public authStore: AuthStore,
    private authService: AuthService,
    public router: Router,
    private cdRef: ChangeDetectorRef,
    private oidcSecurityService: OidcSecurityService,
    library: FaIconLibrary,
    private vcr: ViewContainerRef,
    private markingWarnService: MarkingWarnService,
    private dashboardService: DashboardService,
    private notificationService: NotificationService,
    private markReadNotificationService: MarkReadNotificationService,
    private userService: UserService,
    private adminService: AdminService,
    private appConfigService: AppConfigService
  ) {
    super();
    library.addIcons(faCircleQuestion, faBook, faBell, faCircle, faBars, faUser, faSchool, faArrowRightFromBracket, faUserSecret, faChevronDown, faMagnifyingGlass);
  }

  ngOnInit(): void {
    this.appConfigService.configStatus
      .pipe(
        takeUntil(this.componentDestroyed),
        filter((isLoaded: boolean) => isLoaded)
      )
      .subscribe(() => {
        this.setEnvironmentBannerState();
        this.cdRef.detectChanges();
      });

    this.authStore.UserInfo$.pipe(
      filter((user: User) => !!user),

      take(1)
    ).subscribe((user: User) => {
      this.user = { ...user };
      this.cdRef.detectChanges();
      if (this.enableNotifications) {
        this.startPolling();
      }
      this.populateUserMenu();
    });

    // Once a notification is marked as read, start polling again to refresh the list.
    this.markReadNotificationService.notificationMarked$.subscribe((isView) => {
      this.startPolling();
      if (isView) {
        this.anchor.hide();
        this.isPopoverOpen = false;
      }
    });
  }

  populateUserMenu(): void {
    this.adminService.getMdeUserAgencies().subscribe((data: AgencyList[]) => {
      if (this.user?.agencySk) {
        this.items.push(
          {
            text: this.staffProfile,
            icon: 'user'
          }
        );
      }
      if (data?.length > 0) {
        this.items.push(
          {
            text: this.locationProfile,
            icon: 'school',
            items: data.map((agency: AgencyList) => { return { text: agency.siteName, agencySk: agency.agencySk } }),
          }
        );
      }
      if (this.displayImpersonationOption && !this.authStore.isImpersonating()) {
        this.items.push(
          {
            text: this.startImpersonationText,
            icon: 'user-secret'
          }
        );
      }
      if (this.authStore.isImpersonating()) {
        this.items.push(
          {
            text: this.stopImpersonationText,
            icon: 'user-secret'
          }
        );
      }
      if (this.items.length > 0) {
        this.items.push(
          {
            separator: true
          }
        );
      }

      this.items.push(
        {
          text: this.logOut,
          icon: 'arrow-right-from-bracket'
        },
      );
    });
  }

  logout(): void {
    sessionStorage.removeItem('impersonationToken');
    this.authService.logout();
    //this.userProfilePopoverAnchor.toggle();
  }

  login(): void {
    if (this.authStore.isImpersonating()) {
      this.authStore.stopImpersonation();
      sessionStorage.removeItem('impersonationToken');
    }

    this.authService.startAuthentication();
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  override afterOnDestroy(): void { }

  openHelp(): void {
    this.router.navigate(['/help-links']);
  }

  search(): void {
    switch (this.searchMode) {
      case 'Student':
        this.openNewReferralModalHandler();
        break;
      case 'Staff':
        break;
      case 'Location':
         this.router
          .navigateByUrl('/', { skipLocationChange: true })
          .then(() => {
            this.router.navigate(['/search/location-search'], {
              state: {
                data: this.searchText,
              }
            });
          });
        break;
      default:
        break;
    }
  }

  openNewReferralModalHandler(): void {
    this.showNewReferralModal$.next(true);
  }

  openNotification(): void {
    this.router.navigate(['/notification']);
  }

  closeModalHandler(): void {
    this.showNewReferralModal$.next(false);
  }

  isShowTopSearchBar(): boolean {
    return some(
      this.user?.permissions,
      (x: Permission) => x?.permissionId === Permissions.CanViewGlobalSearch
    );
  }

  openReleaseNotes(): void {
    const url: string = this.user?.email?.includes('doeexternal')
      ? Constants.releaseNotes.DOE_EXTERNAL
      : Constants.releaseNotes.NON_DOE_EXTERNAL;
    window.open(url, '_blank');
  }

  private setEnvironmentBannerState(): void {
    const config: IAppConfig = this.appConfigService.getConfig();
    const environmentName: string = (config?.environment ?? '').trim().toUpperCase();

    this.showNonProdBanner = environmentName !== 'PROD';
    this.nonProdEnvironmentLabel = environmentName || 'LOWER ENVIRONMENT';

    if (this.showNonProdBanner) {
      document.body.classList.add('non-prod-env');
      return;
    }

    document.body.classList.remove('non-prod-env');
  }

  // Get all notifications and sift through them to get action required and informational ones.
  // As well as which ones are unread/read.
  startPolling(): void {
    this.cdRef.detectChanges();
    this.loading$.next(false);
    this.dashboardService
      .getNotificationSummary()
      .pipe(
        takeUntil(this.componentDestroyed), // remove comp destroy when above code is uncommented
        map((response: NotificationSummaryResponse) => {
          // Map the response as before.
          const result: AllNotificationsResponse[] = orderBy(
            response?.notificationResponse || [],
            ['dateTime'],
            ['desc']
          );

          // Retrieve action required notifcations
          const actionRequired: AllNotificationsResponse[] = result.filter(
            (item: AllNotificationsResponse) =>
              item.type === NotificationType.ActionRequired
          );

          // Retrieve informational notifications
          const informational: AllNotificationsResponse[] = result.filter(
            (item: AllNotificationsResponse) =>
              item.type === NotificationType.Informational
          );
          // this.getBadgeCount(actionRequired, informational);
          this.badgeCountToShow = response.totalNotificationCount;
          this.actionRequiredLength = response.totalActionRequiredCount;
          this.informationalLength = response.totalInformationalCount;
          // this.getTabCount(actionRequired, informational);
          result.forEach((item: AllNotificationsResponse) => {
            item.dateTime = formatNotificationDateTime(
              new Date(item.dateTime), // Date/time from the backend
              new Date() // todays Date
            );
          });
          this.allNotifications = result;
          return { actionRequired, informational };
        }),
        catchError(() => {
          return of({ actionRequired: [], informational: [] }); // Return an empty observable in case of error to keep the polling going.
        })
      )
      .subscribe({
        next: ({
          actionRequired,
          informational,
        }: {
          actionRequired: AllNotificationsResponse[];
          informational: AllNotificationsResponse[];
        }) => {
          this.loading$.next(true);
          this.actionRequired = actionRequired
          // .filter(
          //   (item: AllNotificationsResponse) =>
          //     item.actions === NotificationAction.UnRead ||
          //     item.actions === NotificationAction.Created //show only actions Unread=2/ Created=5.
          // )
          // .slice(this.startIndexPortion, this.endIndexPortion); // Limit to 10 items only.
          this.informational = informational
          // .slice(
          //   this.startIndexPortion,
          //   this.endIndexPortion
          // ); // Limit to 10 items.
          this.cdRef.detectChanges();
        },
      });
  }

  markingNotification(payload: MarkPayload[]): void {
    this.loading$.next(false);
    this.dashboardService
      .markNotification(payload)
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe({
        next: () => {
          this.loading$.next(true);
          this.startPolling(); // Refresh notifications after marking.
          this.notificationService.show({
            content: 'Marking notifications successful.',
            cssClass: 'notification-success',
            hideAfter: dialogTimer,
            position: { horizontal: 'center', vertical: 'top' },
            animation: { type: 'slide', duration: 60 },
            type: { style: 'success', icon: true },
          });
        },
        error: () => {
          this.loading$.next(true);
          this.notificationService.show({
            content: 'Marking notifications failed. Please try again later.',
            cssClass: 'notification-error',
            position: { horizontal: 'center', vertical: 'top' },
            animation: { type: 'slide', duration: 60 },
            type: { style: 'error', icon: true },
          });
        },
      });
  }

  markAllAsDone(selectedTabIndex: number): void {
    const sourceArray = selectedTabIndex === this.actionRequiredTab ? this.actionRequired : selectedTabIndex === this.tabIndexOne
      ? this.informational : [];

    const markAboveAsReadPayload: MarkPayload[] = sourceArray.map(
      (item: AllNotificationsResponse) => ({
        notificationSk: item.notificationSk,
        studentSk: item.studentSK,
        notificationTypeId: item.notificationTypeId,
        notificationStatus: NotificationAction.Read,
        markAllRead: NotificationReadAllBit.All,
        authUserRoleLocationSk: item.authUserRoleLocationSk
      })
    );
    if (selectedTabIndex === this.actionRequiredTab) {
      this.markingWarnService
        .open('markAbove', this.vcr)
        .pipe(take(1))
        .subscribe((result: DialogCloseResult | any) => {
          if (!(result instanceof DialogCloseResult)) {
            this.markingNotification(markAboveAsReadPayload);
          }
        });
    }
    if (selectedTabIndex === this.tabIndexOne) {
      this.markingNotification(markAboveAsReadPayload);
    }
  }

  togglePopover(): void {
    if (!this.isPopoverOpen) this.startPolling();
    this.isPopoverOpen = !this.isPopoverOpen;

    this.anchor.toggle();
  }

  viewAllNotifications(): void {
    this.router.navigate(['/all-notifications']);
    this.togglePopover();
  }

  onSelect(event: SelectEvent): void {
    this.selectedTabIndex = event.index;
  }

  getBadgeCount(
    actionReqNotification: AllNotificationsResponse[],
    infoNotification: AllNotificationsResponse[]
  ): void {
    // Retrieve the count of unread action required notifications
    const unreadActionReqCount: number = actionReqNotification.filter(
      (item: AllNotificationsResponse) =>
        item.actions === NotificationAction.UnRead ||
        item.actions === NotificationAction.Created
    ).length;

    // Retrieve the count of unread informational notifications
    const unreadInfoCount: number = infoNotification.filter(
      (item: AllNotificationsResponse) =>
        item.actions === NotificationAction.UnRead ||
        item.actions === NotificationAction.Created
    ).length;

    // If both counts are zero, no badge is shown
    if (
      unreadActionReqCount === this.emptyCount &&
      unreadInfoCount === this.emptyCount
    ) {
      this.badgeCountToShow = null; // No badge
    }
    // If no unread action required but some unread informational, show a dot badge
    else if (
      unreadActionReqCount === this.emptyCount &&
      unreadInfoCount > this.emptyCount
    ) {
      this.badgeCountToShow = this.dotBadgeCount;
    } // If there are unread action required notifications, show their count (capped at 99)
    else if (unreadActionReqCount > this.emptyCount) {
      this.badgeCountToShow = Math.min(
        unreadActionReqCount,
        maxBadgeCountLimit
      ); // Limit to 99
    }
  }

  getTabCount(
    actionReqNotification: AllNotificationsResponse[],
    infoNotification: AllNotificationsResponse[]
  ): void {
    this.actionRequiredLength = actionReqNotification.filter(
      (item: AllNotificationsResponse) =>
        item.actions === NotificationAction.UnRead ||
        item.actions === NotificationAction.Created //show only actions Unread=2/ Created=5.
    ).length;
    this.informationalLength = infoNotification.length;
  }

  viewProviderProfile(): void {
    this.router.navigateByUrl('/admin/agency-mde/provider-information/' + this.user?.agencySk[0] + '/' + this.user?.providerSk + '/' + this.user?.providerType)
  }

  impersonateUser(): void {
    this.displayImpersonateUserDialog$.next(true);
  }

  stopImpersonation(): void {
    this.userService.endImpersonation().subscribe({
      next: () => {
        this.notificationService.show({
          content: 'Impersonation stopped.',
          hideAfter: dialogTimer,
          cssClass: 'notification-success',
          position: { horizontal: 'center', vertical: 'top' },
          animation: { type: 'slide', duration: 60 },
          type: { style: 'success', icon: true },
        });
        this.authStore.stopImpersonation();
        sessionStorage.removeItem('impersonationToken');
        location.reload();
      },
      error: () => {
        this.notificationService.show({
          content: 'Failed to stop impersonation. Please try again.',
          hideAfter: dialogTimer,
          cssClass: 'notification-error',
          position: { horizontal: 'center', vertical: 'top' },
          animation: { type: 'slide', duration: dialogAnimationDuration },
          type: { style: 'error', icon: true },
        });
      }
    })
  }

  navigateToPermissions(): void {
    this.router.navigate(['/permissions']);
  }
  get displayImpersonationOption(): boolean {
    return some(
      this.user?.selectedRole?.userPermissions,
      (x: Permission) => x.permissionId === Permissions.CanImpersonate
    );
  }

  showImpersonationNotification(): void {
    this.notificationService.show({
      content: this.impersonationTemplate,
      cssClass: 'notification-info impersonation-notification',
      closable: true,
      position: { horizontal: 'center', vertical: 'top' },
      animation: { type: 'slide', duration: 60 },
      type: { style: 'info', icon: false },
    });
  }

  selectUserAction(event: ContextMenuSelectEvent): void {
    switch (event?.item?.text) {
      case this.staffProfile:
        this.viewProviderProfile();
        break;
      case this.startImpersonationText:
        this.impersonateUser()
        break;
      case this.stopImpersonationText:
        this.stopImpersonation()
        break;
      case this.logOut:
        this.logout();
        break;
      default:
        if (event?.item?.agencySk) {
          this.router.navigate([`/location/${event.item.agencySk}/view`]);
        }
        break;
    }
  }

  getPlaceholderText(): string {
    return this.searchMode === this.searchData[0]
      ? 'Search by Student Name or ID'
      : 'Search by Location Name or Code';
  }
}